import { useState, useEffect, useCallback } from 'react';
import { appointmentsService, staffService } from '../services/api';
import { AppointmentModal } from '../components/AppointmentModal';
import { formatApptTime, getDayName, isSameDay, addDays, dateToInputValue, localDateKey } from '../utils/dateFormatters';
import './Calendar.css';

const VIEW_MODES = { DAY: 'day', WEEK: 'week', MONTH: 'month' };
const isMobile = () => window.innerWidth <= 768;

export default function Calendar() {
  const [viewMode, setViewMode] = useState(isMobile() ? VIEW_MODES.DAY : VIEW_MODES.WEEK);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('mine'); // 'mine' | 'all' | staffId
  const [staff, setStaff] = useState([]);
  const [modal, setModal] = useState(null); // { appointment } | { create: true }

  useEffect(() => { staffService.getAll().then((r) => r.success && setStaff(r.staff || [])); }, []);

  function getDateRange() {
    const today = new Date(currentDate);
    if (viewMode === VIEW_MODES.DAY) return { start: today, end: today };
    if (viewMode === VIEW_MODES.WEEK) {
      const start = new Date(today); start.setDate(today.getDate() - today.getDay());
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return { start, end };
    }
    return { start: new Date(today.getFullYear(), today.getMonth(), 1), end: new Date(today.getFullYear(), today.getMonth() + 1, 0) };
  }

  const load = useCallback(async () => {
    setLoading(true);
    const { start, end } = getDateRange();
    const params = filter === 'all' ? { view: 'all' } : filter === 'mine' ? {} : { staffId: filter };
    const res = await appointmentsService.getCalendar(dateToInputValue(start), dateToInputValue(end), params);
    if (res.success) setAppointments(res.appointments || []);
    setLoading(false);
  }, [currentDate, viewMode, filter]);

  useEffect(() => { load(); }, [load]);

  function navigate(dir) {
    const d = new Date(currentDate);
    const step = viewMode === VIEW_MODES.DAY ? 1 : viewMode === VIEW_MODES.WEEK ? 7 : 0;
    if (viewMode === VIEW_MODES.MONTH) d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + dir * step);
    setCurrentDate(d);
  }

  const apptsForDate = (day) => appointments.filter((a) => localDateKey(a.starts_at) === localDateKey(day));
  const weekDays = () => { const { start } = getDateRange(); return Array.from({ length: 7 }, (_, i) => addDays(start, i)); };
  const monthLabel = () => currentDate.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

  function ApptCard({ a, compact }) {
    return (
      <button
        className={`calendar-appt status-${a.status.replace('_', '-')}`}
        style={{ borderLeftColor: a.staff_color || 'var(--color-teal)' }}
        onClick={() => setModal({ appointment: a })}
      >
        <span className="appt-time">{formatApptTime(a.starts_at)}</span>
        <span className="appt-title">{a.service_name || 'Appointment'}</span>
        {!compact && <span className="appt-sub">{a.client_name || '—'}{filter === 'all' ? ` · ${a.staff_name}` : ''}</span>}
      </button>
    );
  }

  return (
    <div className="calendar-page">
      <div className="page-header">
        <h1 className="page-title">Calendar</h1>
        <div className="page-actions">
          <select className="person-filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="mine">My calendar</option>
            <option value="all">Everyone</option>
            {staff.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button className="btn btn-primary" onClick={() => setModal({ create: true })}>+ New</button>
        </div>
      </div>

      <div className="calendar-controls">
        <div className="nav-buttons">
          <button onClick={() => navigate(-1)} className="btn btn-outline">←</button>
          <button onClick={() => setCurrentDate(new Date())} className="btn btn-outline">Today</button>
          <button onClick={() => navigate(1)} className="btn btn-outline">→</button>
        </div>
        <h2 className="calendar-title">{monthLabel()}</h2>
        <div className="view-toggle">
          {Object.entries(VIEW_MODES).map(([key, value]) => (
            <button key={key} className={`view-btn ${viewMode === value ? 'active' : ''}`} onClick={() => setViewMode(value)}>{key}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="calendar-loading"><div className="loading-spinner"></div></div>
      ) : viewMode === VIEW_MODES.WEEK ? (
        <div className="week-view">
          <div className="week-header">
            {weekDays().map((day, i) => (
              <div key={i} className={`week-day-header ${isSameDay(day, new Date()) ? 'today' : ''}`}>
                <span className="day-name">{getDayName(day)}</span>
                <span className="day-number">{day.getDate()}</span>
              </div>
            ))}
          </div>
          <div className="week-grid">
            {weekDays().map((day, i) => (
              <div key={i} className={`week-day ${isSameDay(day, new Date()) ? 'today' : ''}`}>
                {apptsForDate(day).map((a) => <ApptCard key={a.id} a={a} compact />)}
              </div>
            ))}
          </div>
        </div>
      ) : viewMode === VIEW_MODES.DAY ? (
        <div className="day-view">
          <h3 className="day-title">{currentDate.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
          <div className="day-appts">
            {apptsForDate(currentDate).length === 0 ? (
              <div className="empty-state"><p>No appointments this day</p></div>
            ) : apptsForDate(currentDate).map((a) => <ApptCard key={a.id} a={a} />)}
          </div>
        </div>
      ) : (
        <div className="month-view"><p className="text-muted text-center">Month view coming soon…</p></div>
      )}

      <AppointmentModal
        isOpen={!!modal}
        onClose={() => setModal(null)}
        onSave={() => { setModal(null); load(); }}
        appointment={modal?.appointment || null}
        defaultDate={modal?.create ? dateToInputValue(currentDate) : ''}
      />
    </div>
  );
}
